import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectSettings, selectSettingsLoading, selectSettingsError } from '../store/selectors';
import { fetchSettings } from '../store/thunks/settingsThunks';
import { useSession } from './useSession';
import { useLocalization } from '../context/LocalizationContext';

/**
 * Hook to access settings from Redux store with optional automatic fetching
 */
export const useSettingsRedux = (autoFetch: boolean = true) => {
  const dispatch = useAppDispatch();
  const { sessionId, getFormattedExperiments, experimentsLoaded } = useSession();
  const { config, cookieStatus } = useLocalization();

  const settings = useAppSelector(selectSettings);
  const loading = useAppSelector(selectSettingsLoading);
  const error = useAppSelector(selectSettingsError);

  // Auto-fetch settings when session and experiments are available, handshake received, and we don't have settings yet
  useEffect(() => {
    // Wait for handshake to complete (cookieStatus !== null indicates handshake received)
    const handshakeCompleted = cookieStatus !== null;

    if (autoFetch && sessionId && experimentsLoaded && handshakeCompleted && !settings && !loading) {
      const experiments = getFormattedExperiments();
      dispatch(
        fetchSettings({
          language: config.language,
          experiments,
          sessionId: sessionId || undefined,
        })
      );
    }
  }, [
    dispatch,
    sessionId,
    experimentsLoaded,
    cookieStatus,
    config.language,
    settings,
    loading,
    autoFetch,
    getFormattedExperiments,
  ]);

  // Retry settings loading if all conditions are met but settings are still null after a delay
  useEffect(() => {
    if (!autoFetch || settings || loading) return;

    const handshakeCompleted = cookieStatus !== null;
    if (sessionId && experimentsLoaded && handshakeCompleted) {
      // Set a timeout to retry if settings haven't loaded after 2 seconds
      const retryTimeout = setTimeout(() => {
        if (!settings && !loading && sessionId && experimentsLoaded && cookieStatus !== null) {
          const experiments = getFormattedExperiments();
          dispatch(
            fetchSettings({
              language: config.language,
              experiments,
              sessionId: sessionId || undefined,
            })
          );
        }
      }, 1000);

      return () => clearTimeout(retryTimeout);
    }
  }, [
    autoFetch,
    sessionId,
    experimentsLoaded,
    cookieStatus,
    settings,
    loading,
    config.language,
    dispatch,
    getFormattedExperiments,
  ]);

  return {
    settings,
    loading,
    error,
    // Helper to manually refetch settings
    refetch: () => {
      if (sessionId && experimentsLoaded) {
        const experiments = getFormattedExperiments();

        dispatch(
          fetchSettings({
            language: config.language,
            experiments,
            sessionId: sessionId || undefined,
          })
        );
      }
    },
  };
};
